import { useState } from 'react'
import { styled } from 'styled-components'
import { Student, updateStudent } from '../../../libs/student-service'
import { IoClose } from 'react-icons/io5'

interface Props {
    student: Student
    onClose: () => void
    onConfirm: (updatedStudent: Partial<Student>) => void
}

const Overlay = styled.div`
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Modal = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 550px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #f0f0f0;
`

const Title = styled.h2`
    font-size: 20px;
    font-weight: 600;
    color: #000000;
    margin: 0;
`

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999999;
    transition: color 0.2s;

    &:hover {
        color: #333333;
    }

    svg {
        width: 24px;
        height: 24px;
    }
`

const Content = styled.div`
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const Label = styled.label`
    font-size: 14px;
    font-weight: 500;
    color: #666666;
`

const Input = styled.input<{ readOnly?: boolean }>`
    padding: 12px 16px;
    border: 1px solid ${(props) => (props.readOnly ? '#e5e6eb' : '#1fa474')};
    border-radius: 6px;
    font-size: 14px;
    background-color: ${(props) => (props.readOnly ? '#f5f5f5' : '#ffffff')};
    color: #333333;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
        box-shadow: 0 0 0 2px rgba(31, 164, 116, 0.1);
    }

    &:read-only {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }
`

const Select = styled.select`
    padding: 12px 16px;
    border: 1px solid #1fa474;
    border-radius: 6px;
    font-size: 14px;
    background-color: #ffffff;
    color: #333333;
    cursor: pointer;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: #1fa474;
        box-shadow: 0 0 0 2px rgba(31, 164, 116, 0.1);
    }
`

const Footer = styled.div`
    padding: 24px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
`

const ConfirmButton = styled.button`
    padding: 12px 32px;
    background-color: #1fa474;
    color: white;
    border: none;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: #168a5d;
    }

    &:active:not(:disabled) {
        transform: scale(0.98);
    }

    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }
`

const ErrorMessage = styled.div`
    padding: 12px 16px;
    background-color: #fee;
    border: 1px solid #fcc;
    border-radius: 6px;
    color: #c33;
    font-size: 14px;
`

export default function EditStudentModal({
    student,
    onClose,
    onConfirm,
}: Props) {
    const [formData, setFormData] = useState({
        first_name: student.first_name,
        last_name: student.last_name,
        student_id: student.student_id,
        gender: student.gender || '',
    })
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }))
        setError(null)
    }

    const handleConfirm = async () => {
        setLoading(true)
        setError(null)

        try {
            await updateStudent(student.id, {
                first_name: formData.first_name,
                last_name: formData.last_name,
                student_id: formData.student_id,
                gender: formData.gender,
            })

            onConfirm(formData)
            onClose()
        } catch (err) {
            const errorMessage =
                err instanceof Error ? err.message : 'Failed to update student'
            setError(errorMessage)
            console.error('Failed to update student:', err)
        } finally {
            setLoading(false)
        }
    }

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>Edit Student Information</Title>
                    <CloseButton onClick={onClose}>
                        <IoClose />
                    </CloseButton>
                </Header>

                <Content>
                    <FormGroup>
                        <Label>First Name</Label>
                        <Input
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Surname</Label>
                        <Input
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Student ID</Label>
                        <Input
                            type="text"
                            name="student_id"
                            value={formData.student_id}
                            onChange={handleInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label>Gender</Label>
                        <Select
                            name="gender"
                            value={formData.gender}
                            onChange={handleInputChange}
                        >
                            <option value="">
                                Gender List To Be Confirmed
                            </option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                            <option value="Prefer not to say">
                                Prefer not to say
                            </option>
                        </Select>
                    </FormGroup>

                    {error && <ErrorMessage>{error}</ErrorMessage>}
                </Content>

                <Footer>
                    <ConfirmButton onClick={handleConfirm} disabled={loading}>
                        {loading ? 'Saving...' : 'Confirm'}
                    </ConfirmButton>
                </Footer>
            </Modal>
        </Overlay>
    )
}
